/*------------------------------------------------------------------------------
   About      : Template text field with `${CODE}` token chips
                (epic DA-697, II-FE-3)

                ⚠️ The mockups show `{{value}}`, `{{season}}`, `{{appliance}}`.
                Those are NOT tokens. pingpong substitutes only
                `"${" + DerivedQuantity.name() + "}"` — dollar-brace, uppercase,
                the exact enum constant. A `{{…}}` token is never substituted
                and ships literally to end users. So the chip's LABEL is the
                catalog `displayName` ("Usage Δ %") and the INSERTED TEXT is
                `${USAGE_DELTA_PERCENTAGE}`.

                Chips are ordered by `sortOrder`, filtered to
                `usableInTemplateText` and to variables whose
                `allowedInsightTypes` includes the current insight type.

                `BILL_START` / `BILL_END` open a format picker instead of
                inserting directly: the suffix is mandatory, and a bare
                `${BILL_END}` raises ArrayIndexOutOfBoundsException out of
                pingpong's insight computation, uncaught.

                The locale control in the header is deliberately a **disabled**
                switcher, not a working one — see the comment on `pilotLocales`.

                There is no existing token-insertion UI in this repo (the only
                `{{var}}` handling is render-time substitution in
                `components/wysiwyg/PreviewArea.tsx`), so this is a plain
                textarea plus chips. `@monaco-editor/react` is already a
                dependency if completion/decoration is wanted later.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useMemo, useRef, useState } from 'react'
import { Alert, Box, Chip, FormHelperText, Menu, MenuItem, Stack, TextField,
         Tooltip, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import { CMS_DEFAULT_LOCALE,
         selectCMSLocales,
         selectCMSSelectedPilot } from 'store/dataReducers/cmsPilotSlice'

import { InsightCatalog, DerivedQuantityVariable } from '../types/insightTypes'
import { derivedQuantitiesFor,
         extractTokens,
         resolveToken,
         tokenFor,
         tokenForWithDateFormat } from '../utils/insightCatalogHelpers'
import { FIELD_LABEL_SX,
         SECTION_HEADING_SX }    from './insightFormStyles'

interface TemplateTextEditorProps {
  value        : string
  onChange     : (next: string) => void
  catalog     ?: InsightCatalog
  /** Filters the chip list via `allowedInsightTypes`. */
  insightType ?: string | null
  label       ?: string
  maxLength   ?: number
  disabled    ?: boolean
  /** Applied to the input when the field differs from the published version. */
  modified    ?: boolean
  helperText  ?: string
}

export const TemplateTextEditor = ({
  value,
  onChange,
  catalog,
  insightType,
  label      = 'Template text',
  maxLength  = 160,
  disabled,
  modified,
  helperText,
}: TemplateTextEditorProps) => {

  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  /* ── Locale ──────────────────────────────────────────────────────────────
     Shown because authors ask which locale they are editing, and the answer is
     always `en`. The other locales are listed and **disabled**, not hidden:
     `updateAll` has no equivalent of the reco path's `updateLocalisedContent`,
     so a non-`en` edit to templateString is accepted and silently dropped
     (open on DA-2369). A switcher that let you type into `fr` and then lost it
     would be worse than no switcher at all — so it visibly refuses instead. */
  const selectedPilot = useSelector(selectCMSSelectedPilot)
  const localeNames   = useSelector(selectCMSLocales)

  const pilotLocales = useMemo<string[]>(() => {
    const supported = (selectedPilot?.supportedLocales as string[] | undefined) ?? []
    // The default is always offered, even for a pilot that somehow omits it —
    // otherwise the control renders blank and reads as "no locale".
    return supported.includes(CMS_DEFAULT_LOCALE)
      ? supported
      : [CMS_DEFAULT_LOCALE, ...supported]
  }, [selectedPilot])

  const localeLabel = (code: string) =>
    localeNames?.find((entry) => entry.code === code)?.name ?? code

  // Anchor + pending variable for the date-format menu.
  const [formatMenu, setFormatMenu] = useState<{
    anchor   : HTMLElement
    variable : DerivedQuantityVariable
  } | null>(null)

  // Codes already in the text are kept in the chip list even if deprecated, so
  // an existing value never vanishes from its own editor.
  const usedCodes = useMemo(
    () => extractTokens(value).map((raw) => resolveToken(catalog, raw).code),
    [value, catalog],
  )

  const chips = useMemo(
    () => derivedQuantitiesFor(catalog, {
      usage       : 'templateText',
      insightType,
      keepCodes   : usedCodes,
    }),
    [catalog, insightType, usedCodes],
  )

  /**
   * Inserts at the caret rather than appending.
   *
   * Appending would force the author to cut and paste every token into place,
   * which is how `{{value}}`-style hand-typing starts.
   */
  const insertAtCaret = (token: string) => {
    const input = inputRef.current
    const start = input?.selectionStart ?? value.length
    const end   = input?.selectionEnd   ?? value.length

    const next = value.slice(0, start) + token + value.slice(end)
    onChange(next)

    // Restore the caret after the inserted token so the author can keep typing.
    // Deferred a frame because the value round-trips through the parent.
    requestAnimationFrame(() => {
      if (!input) return
      const caret = start + token.length
      input.focus()
      input.setSelectionRange(caret, caret)
    })
  }

  const handleChipClick = (variable: DerivedQuantityVariable, anchor: HTMLElement) => {
    if (variable.dateFormatSuffix) {
      // Never insert a bare date token — open the picker instead.
      setFormatMenu({ anchor, variable })
      return
    }
    insertAtCaret(tokenFor(variable))
  }

  const handleFormatPick = (dateFormat: string) => {
    if (!formatMenu) return
    insertAtCaret(tokenForWithDateFormat(formatMenu.variable, dateFormat))
    setFormatMenu(null)
  }

  // Problems in the CURRENT text, surfaced inline rather than only on save.
  const tokenProblems = useMemo(() => {
    if (!catalog) return []
    const problems : string[] = []
    for (const raw of extractTokens(value)) {
      const resolved = resolveToken(catalog, raw)
      if (!resolved.known) {
        problems.push(`\${${raw}} is not a known variable — users would see it exactly as written.`)
      } else if (resolved.bareDateToken) {
        problems.push(`\${${raw}} needs a date format suffix (e.g. \${${raw}_MONTH_NAME}).`)
      }
    }
    return problems
  }, [value, catalog])

  // Catches the mistake the mockups invite, before it reaches the server.
  const hasHandlebars = /\{\{\s*\w+\s*\}\}/.test(value)

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb : 0.5, minHeight : 40 }}>
        <Typography variant="subtitle2" sx={SECTION_HEADING_SX}>{label}</Typography>
        <Box sx={{ flexGrow : 1 }} />

        <TextField
          select
          size="small"
          label="Locale"
          value={CMS_DEFAULT_LOCALE}
          /* Every non-default option is disabled, so this can only ever be
             re-selected as itself. Present because a controlled select without
             one warns, not because a switch is reachable. */
          onChange={() => undefined}
          disabled={disabled}
          sx={{ minWidth : 140, ...FIELD_LABEL_SX }}
        >
          {pilotLocales.map((code) => {
            const isDefault = code === CMS_DEFAULT_LOCALE

            const item = (
              <MenuItem key={code} value={code} disabled={!isDefault}>
                <Typography variant="body2" noWrap>{localeLabel(code)}</Typography>
              </MenuItem>
            )

            // Tooltip needs a non-disabled child to receive pointer events, so
            // it wraps a span rather than the MenuItem itself.
            return isDefault ? item : (
              <Tooltip
                key={code}
                placement="left"
                arrow
                /* No ticket id in the copy — see the note on `pilotLocales` for
                   what is actually open. */
                title="Template text can only be edited in the default locale. Translated versions are not editable yet."
              >
                <span>{item}</span>
              </Tooltip>
            )
          })}
        </TextField>
      </Stack>

      {!!chips.length && (
        <Stack direction="row" spacing={0.5} sx={{ mb : 1, flexWrap : 'wrap', gap : 0.5 }}>
          {chips.map((variable) => (
            <Tooltip
              key={variable.code}
              placement="top"
              arrow
              title={
                variable.dateFormatSuffix
                  ? `${variable.description || variable.code} — pick a date format`
                  : `Inserts \${${variable.code}}`
              }
            >
              <Chip
                label={variable.displayName}
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={(e) => handleChipClick(variable, e.currentTarget)}
                sx={{ fontSize : '0.75rem' }}
              />
            </Tooltip>
          ))}
        </Stack>
      )}

      {/* The counter sits INSIDE the field, bottom-left, over padding reserved
          for it — so it reads as part of the box being typed in rather than as
          a detached number above it.

          `helperText` is rendered separately below rather than through the
          TextField, which keeps the counter's offset a constant instead of
          something that has to track whether helper text is present. */}
      <Box sx={{ position : 'relative' }}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          inputRef={inputRef}
          error={!!tokenProblems.length || hasHandlebars || value.length > maxLength}
          sx={{
            '& .MuiInputBase-root' : {
              // Room for the counter, so a long sentence never runs under it.
              pb : 3.25,
              ...(modified ? {
                bgcolor : (theme: any) => theme.palette.mode === 'light'
                  ? 'rgba(196, 255, 176, 0.5)'
                  : 'rgba(98, 128, 88, 0.5)',
              } : undefined),
            },
          }}
        />

        <Typography
          variant="caption"
          sx={{
            position      : 'absolute',
            left          : 14,
            bottom        : 8,
            // Click-through, or the counter would steal a click meant to place
            // the caret at the end of the text.
            pointerEvents : 'none',
            color         : value.length > maxLength ? 'error.main' : 'text.secondary',
          }}
        >
          {value.length} / {maxLength}
        </Typography>
      </Box>

      {helperText && <FormHelperText sx={{ mx : 1.75 }}>{helperText}</FormHelperText>}

      {hasHandlebars && (
        <Alert severity="error" sx={{ mt : 1 }}>
          This text contains a <code>{'{{…}}'}</code> placeholder. Only{' '}
          {/* eslint-disable-next-line no-template-curly-in-string -- literal
              token syntax shown to the user, not an un-interpolated template */}
          <code>{'${VARIABLE}'}</code> tokens are replaced with real values — a{' '}
          <code>{'{{…}}'}</code> placeholder reaches users exactly as written.
          Use the chips above.
        </Alert>
      )}

      {tokenProblems.map((problem) => (
        <Alert key={problem} severity="error" sx={{ mt : 1 }}>{problem}</Alert>
      ))}

      <Menu
        open={!!formatMenu}
        anchorEl={formatMenu?.anchor ?? null}
        onClose={() => setFormatMenu(null)}
      >
        <MenuItem disabled sx={{ opacity : 1 }}>
          <Typography variant="caption" sx={{ fontWeight : 600 }}>Date format</Typography>
        </MenuItem>
        {(formatMenu?.variable.supportedDateFormats ?? []).map((dateFormat) => (
          <MenuItem key={dateFormat} onClick={() => handleFormatPick(dateFormat)}>
            <Typography variant="body2" sx={{ fontFamily : 'monospace' }}>{dateFormat}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default TemplateTextEditor
